<template>
  <div class="recommandations-page mandat-page">
    <header class="mandat-page__hero">
      <p class="mandat-page__eyebrow">Mandat</p>
      <h1 class="mandat-page__title">Liste des recommandations</h1>
      <p class="mandat-page__subtitle">
        Vue d’ensemble des actions mandat : clients, conseillers, échéances et statuts.
      </p>
    </header>

    <div class="mandat-page__toolbar">
      <div class="mandat-page__search">
        <span class="mandat-page__search-icon" aria-hidden="true">
          <i class="fas fa-search"></i>
        </span>
        <input
          type="search"
          class="form-control"
          placeholder="Rechercher…"
          v-model="searchQuery"
          autocomplete="off"
        >
      </div>
    </div>

    <div class="mandat-table-wrap">
      <div v-if="errorMessage" class="alert alert-danger mandat-alert">{{ errorMessage }}</div>
      <div v-if="loading" class="mandat-loading text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement…</span>
        </div>
      </div>
      <div v-else-if="recommendations.length === 0" class="alert alert-info mandat-empty">
        Aucune recommandation trouvée.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Client</th>
              <th>Employé</th>
              <th>Date de création</th>
              <th>Date d'échéance</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedItems" :key="item.id">
              <td>{{ item.titre }}</td>
              <td>{{ item.client?.login || item.client?.username || '—' }}</td>
              <td>{{ item.employe?.login || item.employe?.username || '—' }}</td>
              <td>{{ formatDate(item.date_creation) }}</td>
              <td>{{ formatDate(item.date_echeance) }}</td>
              <td>
                <span :class="getStatusClass(item.statut)">{{ formatStatut(item.statut) }}</span>
              </td>
              <td>
                <button type="button" class="mandat-icon-btn mandat-icon-btn--view me-1" @click="viewDetails(item)" title="Voir les détails">
                  <i class="fas fa-eye"></i>
                </button>
                <button type="button" class="mandat-icon-btn mandat-icon-btn--edit me-1" @click="editItem(item)" title="Modifier">
                  <i class="fas fa-edit"></i>
                </button>
                <button type="button" class="mandat-icon-btn mandat-icon-btn--danger" @click="deleteItem(item)" title="Supprimer">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="mandat-pagination">
          <div class="mandat-pagination__size">
            <label for="page-size-all" class="mb-0">Afficher</label>
            <select id="page-size-all" v-model="itemsPerPage" class="form-select form-select-sm">
              <option v-for="n in pageSizes" :key="n" :value="n">{{ n }}</option>
            </select>
            <span>éléments</span>
          </div>
          <div class="mandat-pagination__nav">
            <span>
              {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredItems.length) }}
              sur {{ filteredItems.length }}
            </span>
            <div class="btn-group">
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
                Précédent
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import axios from 'axios';
  import Swal from 'sweetalert2';
  import {
    formatMandatDate,
    formatMandatStatut,
    mandatStatutClass,
    MANDAT_PAGE_SIZES,
  } from '@/utils/mandatStatus';
  
  const TOKEN_KEYS = {
    ACCESS: 'auth_session',
    REFRESH: null /* httpOnly cookie */,
    ROLE: 'user_role'
  };
  
  export default {
    name: 'RecommandationList',
    data() {
      return {
        recommendations: [],
        searchQuery: '',
        loading: false,
        errorMessage: null,
        currentPage: 1,
        itemsPerPage: 10,
        pageSizes: MANDAT_PAGE_SIZES
      }
    },
    computed: {
      filteredItems() {
        if (!this.searchQuery) return this.recommendations;
        
        const query = this.searchQuery.toLowerCase();
        return this.recommendations.filter(item => 
          String(item.titre || '').toLowerCase().includes(query) ||
          (item.client?.login || item.client?.username || '').toLowerCase().includes(query) ||
          (item.employe?.login || item.employe?.username || '').toLowerCase().includes(query) ||
          String(item.statut || '').toLowerCase().includes(query)
        );
      },
      totalPages() {
        return Math.ceil(this.filteredItems.length / this.itemsPerPage);
      },
      paginatedItems() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredItems.slice(start, end);
      }
    },
    watch: {
      itemsPerPage() {
        this.currentPage = 1;
      },
      searchQuery() {
        this.currentPage = 1;
      }
    },
    mounted() {
      this.fetchRecommendations();
    },
    methods: {
      async fetchRecommendations() {
        this.loading = true;
        this.errorMessage = null;
        
        try {
          const response = await axios.get("/api/actions-recommandations/", { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
  
          // La réponse est directement un tableau de recommandations
          this.recommendations = response.data.recommandations || [];
        } catch (error) {
          console.error('Erreur:', error);
          if (error.response?.status === 401) {
            this.errorMessage = "Token non trouvé, veuillez vous connecter.";
            return;
          } else {
            this.errorMessage = error.response?.data?.detail || "Une erreur est survenue lors de la récupération des recommandations.";
          }
        } finally {
          this.loading = false;
        }
      },
      formatDate(date) {
        return formatMandatDate(date);
      },
      formatStatut(statut) {
        return formatMandatStatut(statut);
      },
      getStatusClass(statut) {
        return mandatStatutClass(statut);
      },
      viewDetails(item) {
        this.$router.push(`/actions-mandat/actions-recommandations/${item.id}`);
      },
      editItem(item) {
        this.$router.push(`/actions-mandat/actions-recommandations/${item.id}/modifier`);
      },
      async deleteItem(item) {
        try {
          const result = await Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Cette action est irréversible !",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--bank-danger)',
            cancelButtonColor: 'var(--bank-navy)',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
          });
  
          if (result.isConfirmed) {
            await axios.delete(`/api/actions-recommandations/${item.id}/`, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
  
            await Swal.fire(
              'Supprimé !',
              'La recommandation a été supprimée.',
              'success'
            );
  
            this.fetchRecommendations();
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          Swal.fire(
            'Erreur !',
            'Une erreur est survenue lors de la suppression.',
            'error'
          );
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .table {
    min-width: 720px;
  }
  </style>
  
